// ─────────────────────────────────────────────────────────────────────────────
// facebookAdConstants.js
// Single source of truth for Facebook ad types, verbiage templates, and helpers.
// Place at: src/constants/facebookAdConstants.js
// Imported by: ClientProfile.js, AdminDashboard.js
// ─────────────────────────────────────────────────────────────────────────────

// ── Verbiage template text files ─────────────────────────────────────────────
import dailyNewsTemplate from '../assets/verbiage-templates/Daily News.txt';
import ninetyNineDownTemplate from '../assets/verbiage-templates/99 Down.txt';
import seventyTwoHours from '../assets/verbiage-templates/72 Hour Blowout.txt';
import creditApprovalTemplate from '../assets/verbiage-templates/100 Percent Credit Approval.txt';
import creditPaloozaTemplate from '../assets/verbiage-templates/Credit Palooza.txt';
import dollarDays from '../assets/verbiage-templates/Dollar Days.txt';
import dollarDown from '../assets/verbiage-templates/Dollar Down.txt';
import dontGetTariffied from '../assets/verbiage-templates/Dont Get Tariffied.txt';
import doubleTax from '../assets/verbiage-templates/Double Tax.txt';
import earlyExit from '../assets/verbiage-templates/Early Exit.txt';
import employeePricingSpanish from '../assets/verbiage-templates/Employee Pricing Spanish.txt';
import employeePricing from '../assets/verbiage-templates/Employee Pricing.txt';
import freshStartCredit from '../assets/verbiage-templates/Fresh Start Credit Event.txt';
import getApprovedTodayV2 from '../assets/verbiage-templates/Get Approved Today V2.txt';
import getApprovedToday from '../assets/verbiage-templates/Get Approved Today.txt';
import giveawayAlert from '../assets/verbiage-templates/Giveaway Alert.txt';
import immediatePaymentReduction from '../assets/verbiage-templates/Immediate Payment Reduction.txt';
import laborDaySalesEvent from '../assets/verbiage-templates/Labor Day Sales Event.txt';
import makeDeal from '../assets/verbiage-templates/Make A Deal.txt';
import massiveCreditForgiveness from '../assets/verbiage-templates/Massive Credit Forgiveness.txt';
import massiveCreditForgivenessV2 from '../assets/verbiage-templates/Massive Credit Forgiveness V2.txt';
import massive from '../assets/verbiage-templates/Massive Sales Event.txt';
import massiveSignDrive from '../assets/verbiage-templates/Massive Sign and Drive.txt';
import memorialDayBlowout from '../assets/verbiage-templates/Memorial Day Blowout.txt';
import memorialDay from '../assets/verbiage-templates/Memorial Day.txt';
import newCarBlowout from '../assets/verbiage-templates/New Car Blowout.txt';
import noDownPayment from '../assets/verbiage-templates/No Down Payment.txt';
import payoffTrade from '../assets/verbiage-templates/Payoff Trade.txt';
import preTariffLiquidation from '../assets/verbiage-templates/Pre Tariff Liquidation.txt';
import repoSale from '../assets/verbiage-templates/Repo Sale.txt';
import saturdayOnly from '../assets/verbiage-templates/Saturday Only Liquidation Sale.txt';
import signDrive from '../assets/verbiage-templates/Sign & Drive.txt';
import spanishNoDown from '../assets/verbiage-templates/Spanish No Down Payment.txt';
import swapKeysTradeUp from '../assets/verbiage-templates/Swap Keys and Trade Up.txt';
import tariffFreeSalesEvent from '../assets/verbiage-templates/Tariff Free Sales Event.txt';
import taxTimeSellOff from '../assets/verbiage-templates/Tax Time Sell Off.txt';
import tripleZeroEvent from '../assets/verbiage-templates/Triple Zero Event.txt';
import ultimateCreditEvent from '../assets/verbiage-templates/Ultimate Credit Event.txt';
import upsideDownPayoff from '../assets/verbiage-templates/Upside Down Payoff.txt';
import usedCarBlowoutSpanish from '../assets/verbiage-templates/Used Car Blowout Spanish.txt';
import usedCarBlowout from '../assets/verbiage-templates/Used Car Blowout.txt';
import vehicleAcquisition from '../assets/verbiage-templates/Vehicle Acquisition.txt';
import weSayYes from '../assets/verbiage-templates/We Say Yes.txt';
import jobIsCreditV3 from '../assets/verbiage-templates/Your Job Is Your Credit V3.txt';
import jobIsCreditV2 from '../assets/verbiage-templates/Your Job Is Your Credit V2.txt';
import jobIsCredit from '../assets/verbiage-templates/Your Job Is Your Credit.txt';
import $500GuaranteedApproval from '../assets/verbiage-templates/$500 Guaranteed Approval.txt';
import Spanish$500GuaranteedApproval from '../assets/verbiage-templates/Spanish $500 Guaranteed Approval.txt';
import FourtyEightHourBlowout from '../assets/verbiage-templates/48 Hour Blowout.txt';
import genericDisclaimerVerbiage from '../assets/verbiage-templates/Generic Disclaimer Verbiage.txt';
import directToPublic from '../assets/verbiage-templates/Direct To Public Sell Off.txt';
import emojis from '../assets/verbiage-templates/Emojis.txt';
import biggest from '../assets/verbiage-templates/Biggest Car Sale.txt';
import msrp from '../assets/verbiage-templates/MSRP Buyback.txt';
import redTag from '../assets/verbiage-templates/Red Tag Sales Event.txt';
import presidents from '../assets/verbiage-templates/Presidents Day.txt';
import morganGeneric from '../assets/verbiage-templates/Morgan Auto Generic Disclaimer.txt';
import morganSpecial from '../assets/verbiage-templates/Morgan Auto Special Disclaimer.txt';
import kunes from '../assets/verbiage-templates/Kunes Auto Group Disclaimer.txt';
import allCredit from '../assets/verbiage-templates/All Credit Welcome.txt';
import buybackEvent from '../assets/verbiage-templates/Buyback Event.txt';

// ── Verbiage template map ─────────────────────────────────────────────────────
// To add a new template: import the .txt above, then add a key here.
export const VERBIAGE_TEMPLATES = {
  'Emojis': emojis,
  'Generic Disclaimer Verbiage': genericDisclaimerVerbiage,
  'Morgan Auto Generic Disclaimer': morganGeneric,
  'Morgan Auto Special Disclaimer': morganSpecial,
  'Kunes Auto Group Disclaimer': kunes,
  '48 Hour Blowout': FourtyEightHourBlowout,
  '72 Hour Blowout': seventyTwoHours,
  '99 Down': ninetyNineDownTemplate,
  '100 Percent Credit Approval': creditApprovalTemplate,
  'All Credit Welcome': allCredit,
  'Biggest Car Sale': biggest,
  'Buyback Event': buybackEvent,
  'Credit Palooza': creditPaloozaTemplate,
  '$500 Guaranteed Approval': $500GuaranteedApproval,
  'Spanish $500 Guaranteed Approval': Spanish$500GuaranteedApproval,
  'Daily News': dailyNewsTemplate,
  'Direct To Public Sell Off': directToPublic,
  'Dollar Days': dollarDays,
  'Dollar Down': dollarDown,
  'Dont Get Tariffied': dontGetTariffied,
  'Double Tax': doubleTax,
  'Early Exit': earlyExit,
  'Employee Pricing Spanish': employeePricingSpanish,
  'Employee Pricing': employeePricing,
  'Fresh Start Credit Event': freshStartCredit,
  'Get Approved Today': getApprovedToday,
  'Get Approved Today V2': getApprovedTodayV2,
  'Giveaway Alert': giveawayAlert,
  'Immediate Payment Reduction': immediatePaymentReduction,
  'Labor Day Sales Event': laborDaySalesEvent,
  'Lets Make A Deal': makeDeal,
  'Massive Credit Forgiveness': massiveCreditForgiveness,
  'Massive Credit Forgiveness V2': massiveCreditForgivenessV2,
  'Massive Sales Event': massive,
  'Massive Sign & Drive': massiveSignDrive,
  'Memorial Day Blowout': memorialDayBlowout,
  'Memorial Day': memorialDay,
  'MSRP Buyback': msrp,
  'New Car Blowout': newCarBlowout,
  'No Down Payment': noDownPayment,
  'Payoff Trade': payoffTrade,
  'Presidents Day': presidents,
  'Pre-Tariff Liquidation': preTariffLiquidation,
  'Red Tag Sales Event': redTag,
  'Repo Sale': repoSale,
  'Saturday Only': saturdayOnly,
  'Sign & Drive': signDrive,
  'Spanish No Down Payment': spanishNoDown,
  'Swap Keys & Trade-Up': swapKeysTradeUp,
  'Tariff Free Sales Event': tariffFreeSalesEvent,
  'Tax Time Sell Off': taxTimeSellOff,
  'Triple Zero Event': tripleZeroEvent,
  'Ultimate Credit Event': ultimateCreditEvent,
  'Upside Down Payoff': upsideDownPayoff,
  'Used Car Blowout Spanish': usedCarBlowoutSpanish,
  'Used Car Blowout': usedCarBlowout,
  'Vehicle Acquisition': vehicleAcquisition,
  'We Say Yes': weSayYes,
  'Job Is Credit': jobIsCredit,
  'Job Is Credit V2': jobIsCreditV2,
  'Job Is Credit V3': jobIsCreditV3,
};

// ── Available Facebook Ad Types by year/month ─────────────────────────────────
// To add a new month's ads: add entries to the array for that month.
// New year: add a new top-level key with all 12 months.
export const AVAILABLE_AD_TYPES = {
  '2025': {
    'January': ['JobIsCreditRedBlack'],
    'February': [],
    'March': ['NoDownPaymentRed'],
    'April': ['RepoSaleSpring'],
    'May': ['NoDownSpanishHalloween', 'NoDownPaymentMemorialDay', 'NoDownConstructionBlackYellow'],
    'June': ['GetApprovedTodayBeach', 'JobIsCreditRusticWood', 'KeyForKeyPurpleOrange', 'KeyForKeyPurpleOrangeWithCar', 'LowerYourCarPaymentIllustration',
      'PayoffYourTradeIllustration', 'RepoSaleOrangePink', 'RepoSaleRetro', 'SpanishNoDownNewYear', 'SwapKeysTradeUpIllustrationComic'],
    'July': ['DollarDownFireworks', 'JobIsCreditIllustrationSuv', 'JobIsCreditIllustrationTruck', 'NoDownSpanishSummer', 'NoDownSummerBeach', 'RepoSaleSummer', 'SwapKeysPatriotic',
      'UpsideDownPatriotic', 'UsedCarBlowoutFireworks', 'ZeroDownFourthOfJuly'],
    'August': ['DollarCreditApprovalPaper', 'EarlyExit', 'EndSummerCredit', 'GiveawayAlert', 'JobIsCreditPaperWithKey', 'JobIsCreditPrimaryColors', 'DollarDownPatriotic', 'KickoffSavings', 'LaborDay', 'MassivePayoffBlocks', 'NoDownPaymentLaborDay', 'MassiveRepoSalePrimaryColors', 'NoDownBackToSchoolBlackboard', 'NoDownBackToSchoolChalkboard',
      'NoDownSpanishPatriotic', 'NoDownYellowBlue', 'PayoffYourTradePurpleYellow', 'RepoSaleFootball', 'TradeInTradeUpPatriotic', 'UpsideDownBlueYellow', 'UpsideDownPurpleYellow'],
    'September': ['DollarDaysFall', 'GuaranteedApprovalPaper', '48HourBlowout', 'NoDownPaymentFall', 'RepoSaleRedFootball', 'NoDownPaymentNeonV1', 'NoDownPaymentNeonV2', 'NoDownSpanishRedFootball', 'NoCreditCheckRedYellow', 'UpsideDownFireworks', '100%CreditApprovalRed', 'DontGetTariffied', 'GetApprovedNowFall', 'MassiveCreditForgiveness'],
    'October': ['DriveWith1DollarFall', 'MonsterUsedCarBlowout', 'NoDownSpanishHalloween', 'NoDownSpanishFall', 'UpsideDownFall', 'UpsideDownFall3Vehicles', 'DollarDownSpooky', 'RepoSaleHalloween', 'MonsterSavings', 'UpsideDownPayoffPink', 'RepoSalePrimary', 'UsedCarBlowoutFall', 'RepoSaleRedWhiteBlue', 'CDJR-CoOpDriveIntoFall', 'SwapKeysNeon'],
    'November': ['NoDownSpanishFall', 'JobIsCreditBlackFriday', 'RepoSaleBaseballFire', 'BlackFridayNoDown', 'BlackFridayRepoStyle', 'EarlyExitFall', 'FallDollarDown', 'UsedCarBlowoutFall', 'TradeInTradeUpBlackRed', 'BlackFridayNeonNoDown', 'BlackFridayTripleZeroGold', 'CDJR-CoOpWinter', 'BlackFridayUpsideDownCaution', 'BlackFridayStartsNow'],
    'December': ['100%CreditApprovalWinter', '100%CreditCashBackground', 'DollarDownWinterSimple', 'UpsideDownWinter', 'NewCarBlowout', 'NoDownChristmas', 'NoDownElfHat', 'TradeInTradeUpSantaHat', 'RepoSaleWinter', 'GuaranteedApprovalChristmas', 'UsedCarSpanishChristmas', 'JobIsCreditChristmas', 'EarlyExitWinter', 'DollarDownGoldChristmas',
      'GetApprovedCash', 'GetApprovedChristmas', 'UsedCarBlowoutChristmas', 'EarlyExitNewYear', 'GreenMakeADeal', 'RepoSaleFireworks', 'SwapKeysTradeUpHoliday', 'CandyCaneBlowout', 'JobIsCreditChristmasBow'],
  },
  '2026': {
    'January': ['100%CreditApprovalWinter', 'FreshStartCreditEvent', 'NoDownNewYear', 'SpanishNoDownNewYear', 'PayoffTradeFireworks', 'JobIsCreditPinkGradient', 'SpanishNoDownPaymentRed', 'MSRP', 'MassiveCreditForgivenessCash', 'JobIsCreditWinter', 'JobIsCreditNewYear', 'DollarDownNewYearsSimple', 'SwapKeysFireworks', '100%CreditApprovalNewYear', 'UpsideDownNewYear'],
    'February': ['NoDownPaymentPatriotic', 'NoDownPaymentPatrioticUncleSam', 'SpanishNoDownPaymentPatriotic', 'NoDownPaymentTax', 'NoDownPaymentValentines', 'UsedCarBlowout', 'SwapKeysOrangePink', 'RepoSaleValentines', 'LoveFirstDrive', 'PresidentsDaySalesEvent', 'RepoSaleTaxTime', 'WinterTripleZero', 'JobIsCreditTaxTime', 'DriveWith1DollarComic'],
    'March': ['NoDownMarchMadness', 'RepoSaleMarchMadness', 'DollarDownDollar', 'DollarDownNeonBricks', 'MarchMadnessMSRP', 'MegaUsedCar', 'UsedCarBlowoutSpring', 'CreditForgivenessEvent', 'AutosUsadosExplosion', 'JobIsCreditOrangeSpiral', '100%CreditApprovalPatriotic', 'DriveWith1DollarSpring', '100%CreditApprovalSpring', 'SignDriveGold', 'SignDriveSpring', 'NoDownPaymentSpringPop', 'UpsideDownSpring'],
    'April': ['500ForAnyone', 'UpsideDownSummer', 'UpsideDownSummerV2', 'UpsideDownBeach', 'RepoSaleMarchMadnessV2', 'CeroEngancheCinco', 'GetApprovedTodayComic', 'JobIsCreditSummer', 'NoCreditCheckYellowPink', 'JobIsCreditComic99Down', 'NoDownPaymentComic', 'SpringFling', 'PayoffYourTradeIllustration', 'UsedCarBlowoutCashCash', 'NoDownPaymentRedYellowBlue', 'UltimateCreditForgivenessEvent', 'UltimateCreditRebuildEvent'],
    'May': ['UpsideDownPatriotic', 'SignDrivePatriotic', 'RepoSaleMemorialDay', 'MemorialDay'],
    'June': ['100%CreditApprovalSummer', '100%CreditApprovalPink', 'Zer0DownSummer', 'PayoffTradeComicPatriotic', 'SwapKeysSummer', 'SpanishUsedCarFireworks', '100%CreditApprovalSummerV2', 'UsedCarBlowoutYellowRed', 'MassivePayoffTradeFireworks', 'NoDownPaymentPatriotic', 'JobIsCredit4thOfJuly'],
    'July': ['RepoSaleFireworksPatriotic', 'SignDrivePatriotic', 'SpanishSummerBlowout', 'SummerUsedCarBlowout', '72HourBlowout', 'EverythingMustGo'],
    'August': ['UpsideDownFiesta', 'MassiveCreditForgiveness', 'KeyForKeySummer', 'JobIsCreditRedYellow', 'NoDownBack2School', 'NoDownChalk', 'SignDriveGold', 'NoDownPaymentBlingGreen', 'NoDownPaymentRetroGlow', 'EarlyExitMoney', 'FreshStartCreditGreen', 'UsedCarSpanishConfetti'],
    'September': ['NoDownPaymentLaborDay', 'NoDownPaymentPulse', 'NoDownPaymentFall', 'JobIsCreditBlueLightning', 'NoDownPaymentFallV2', 'GetApprovedToday', 'RepoSaleFall', 'UsedCarBlowoutFall', '72HourBlowoutFall', 'NoDownPaymentFiesta', '100%CreditApprovalFall', 'GetApprovedFall'],
    'October': ['NoDownPaymentHalloween', 'RepoSaleHalloween', 'UpsideDownPayoffHalloween', 'PayoffTradeFall', '100%CreditApprovalFall', 'JobIsCreditBlackOrange'],
    'November': ['GetApprovedNowFall', 'JobIsCreditGreenBling', 'NoDownPaymentDealNoDeal'],
    'December': [],
  },
};

// ── Unicode helpers ───────────────────────────────────────────────────────────
export const toBoldUnicode = (text) => {
  const boldChars = {
    'A':'𝗔','B':'𝗕','C':'𝗖','D':'𝗗','E':'𝗘','F':'𝗙','G':'𝗚','H':'𝗛','I':'𝗜','J':'𝗝',
    'K':'𝗞','L':'𝗟','M':'𝗠','N':'𝗡','O':'𝗢','P':'𝗣','Q':'𝗤','R':'𝗥','S':'𝗦','T':'𝗧',
    'U':'𝗨','V':'𝗩','W':'𝗪','X':'𝗫','Y':'𝗬','Z':'𝗭',
    'a':'𝗮','b':'𝗯','c':'𝗰','d':'𝗱','e':'𝗲','f':'𝗳','g':'𝗴','h':'𝗵','i':'𝗶','j':'𝗷',
    'k':'𝗸','l':'𝗹','m':'𝗺','n':'𝗻','o':'𝗼','p':'𝗽','q':'𝗾','r':'𝗿','s':'𝘀','t':'𝘁',
    'u':'𝘂','v':'𝘃','w':'𝘄','x':'𝘅','y':'𝘆','z':'𝘇',
    '0':'𝟬','1':'𝟭','2':'𝟮','3':'𝟯','4':'𝟰','5':'𝟱','6':'𝟲','7':'𝟳','8':'𝟴','9':'𝟵',
    ' ':' ', '.':'.', ',':',', '-':'-', ':':':', '!':'!', '?':'?',
  };
  return text.split('').map(c => boldChars[c] || c).join('');
};

export const toSuperscriptUnicode = (text) => {
  const superscriptChars = {
    '0':'⁰','1':'¹','2':'²','3':'³','4':'⁴','5':'⁵','6':'⁶','7':'⁷','8':'⁸','9':'⁹',
    'a':'ᵃ','b':'ᵇ','c':'ᶜ','d':'ᵈ','e':'ᵉ','f':'ᶠ','g':'ᵍ','h':'ʰ','i':'ⁱ','j':'ʲ',
    'k':'ᵏ','l':'ˡ','m':'ᵐ','n':'ⁿ','o':'ᵒ','p':'ᵖ','r':'ʳ','s':'ˢ','t':'ᵗ',
    'u':'ᵘ','v':'ᵛ','w':'ʷ','x':'ˣ','y':'ʸ','z':'ᶻ',
    'A':'ᴬ','B':'ᴮ','C':'ᶜ','D':'ᴰ','E':'ᴱ','F':'ᶠ','G':'ᴳ','H':'ᴴ','I':'ᴵ','J':'ᴶ',
    'K':'ᴷ','L':'ᴸ','M':'ᴹ','N':'ᴺ','O':'ᴼ','P':'ᴾ','R':'ᴿ','S':'ˢ','T':'ᵀ',
    'U':'ᵁ','V':'ⱽ','W':'ᵂ',
    '+':'⁺','-':'⁻','=':'⁼','(':'⁽',')':'⁾',' ':' ','*':'*','.':'.','#':'#',
  };
  return text.split('').map(c => superscriptChars[c] || c).join('');
};